import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

const debugModeQueryKey = 'debug-mode';

export function useDebugMode() {
  const query = useQuery<boolean>({
    queryKey: [debugModeQueryKey],
    queryFn: () => {
      if (typeof window === 'undefined') {
        // In case of server-side rendering
        return false;
      }
      const debugMode = localStorage.getItem(debugModeQueryKey);
      return debugMode === 'true';
    },
  });

  return query.isLoading ? false : (query.data ?? false);
}

type DebugModeMutatorFn = (old: boolean) => boolean;
type DebugModeMutator = DebugModeMutatorFn | boolean;

function getNewDebugModeValue(current: boolean, mutator: DebugModeMutator) {
  if (typeof mutator === 'boolean') {
    return mutator;
  } else if (typeof mutator === 'function') {
    return mutator(current);
  }

  return current;
}

export function useSetDebugMode() {
  const queryClient = useQueryClient();
  const current = useDebugMode();

  return useCallback(
    (mutator: DebugModeMutator) => {
      if (typeof current === 'undefined') {
        return;
      }
      const newValue = getNewDebugModeValue(current, mutator);
      queryClient.setQueryData([debugModeQueryKey], newValue);
      localStorage.setItem(debugModeQueryKey, newValue.toString());
    },
    [queryClient, current]
  );
}

export function useToggleDebugMode() {
  const setDebugMode = useSetDebugMode();
  return useCallback(() => {
    setDebugMode((old) => !old);
  }, [setDebugMode]);
}
